import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { useAppDispatch } from '../../../../../../hooks/redux';
import { CustomMUIButton } from '../../../../buttons/CustomMUIButton';
import { ButtonClassNames, ModalVariants } from '../../../../../../models/Common';
import { useCustomSnackbar } from '../../../../../../hooks/useCustomEnqueueSnackbar';
import { userInfoApi } from '../../../../../../services/apiServices/user/userInfo';
import { modalSlice } from '../../../../../../store/reducers/ModalSlice';
import { ApiError } from '../../../../../../models/ApiError';
import { FormInputPassword } from '../../../../../inputs/FormInputPassword';
import { getErrorMessage } from '../../../../../../utils';

interface DeleteEmailProps {
  email: string;
  handleClose: () => void;
}

interface IDeleteEmailForm {
  email: string;
  currentPassword: string;
}

export const DeleteEmail: FC<DeleteEmailProps> = ({ email, handleClose }) => {
  const { t } = useTranslation();
  const methods = useForm<IDeleteEmailForm>();
  const { setError, handleSubmit } = methods;
  const { customEnqueueSnackbar } = useCustomSnackbar();
  const [deleteEmail, { isLoading }] = userInfoApi.useDeleteEmailMutation();
  const dispatch = useAppDispatch();
  const { openModal } = modalSlice.actions;

  const handleDeleteEmail = async ({ currentPassword }) => {
    try {
      await deleteEmail({ currentPassword })
        .unwrap()
        .then(() => {
          customEnqueueSnackbar(t('snackbar.emailDeleted'), 'warning');
          handleClose();
        });
    } catch (err) {
      const error = err as ApiError;
      if (error?.data?.error?.errors) {
        const apiErrors = error.data.error.errors as Partial<Record<keyof IDeleteEmailForm, string>>;
        const apiErrorsKeys = Object.keys(apiErrors) as Array<keyof IDeleteEmailForm>;
        apiErrorsKeys.forEach((element) => {
          setError(element, {
            type: 'server',
            message: apiErrors[element],
          });
        });
      } else {
        const message = getErrorMessage(err);
        customEnqueueSnackbar(message, 'error');
      }
    }
  };

  const handleForgotPassword = () => {
    dispatch(openModal({ modalVariants: ModalVariants.LogOut }));
  };

  return (
    <div className="modals">
      <div className="modals__header">
        <article className="modals__header__title">{t('accountSettings.delete?')}</article>
        <p className="modals__header__description">
          {t('accountSettings.wantDeleteEmail')} <strong>{email}</strong>?
        </p>
      </div>
      <form className="modals__form" onSubmit={handleSubmit(handleDeleteEmail)}>
        <FormProvider {...methods}>
          <FormInputPassword
            name="currentPassword"
            className="primary"
            label={t('auth.enterPassLk')}
            isRequired
            minLength={6}
            maxLength={16}
            fieldHasName
          />
          <div className="modals__btns-wrapper">
            <div className="modals__btn-group">
              <CustomMUIButton
                label={t('buttons.delete')}
                type="submit"
                className={ButtonClassNames.PrimaryFulfilled}
                isBold
                isLoading={isLoading}
              />
            </div>
            <div className="modals__btn-group">
              <CustomMUIButton
                label={t('auth.forgotMyPassword')}
                handleClick={handleForgotPassword}
                className={ButtonClassNames.SecondaryOutlined}
                isBold
                type="button"
              />
            </div>
          </div>
        </FormProvider>
      </form>
    </div>
  );
};
